/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
/* eslint-disable security/detect-object-injection */

import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import {
    ProductSearchResult,
    ProductWarehouseInventoryAvailability,
    DiscountLine,
    CommerceList,
    LoyaltyCard,
    ProductPrice
} from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    getDeliveryOptionsAsync,
    getEstimatedAvailabilityAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ProductComponent } from '../../../themes/dobbies/views/components/product.component';
import {
    IDobbiesSearchResultContainerResources,
    IDobbiesSearchResultContainerConfig,
    ITilesForPLPData
} from '../dobbies-search-result-container.props.autogenerated';
import { getDateObjFromString } from '../../../themes/dobbies/utilities/formatDate';
import { ICategoryScheduledAds } from '../actions/get-scheduled-ads';

interface IProductSearchResultItems {
    products: ProductSearchResult[];
    context: ICoreContext;
    resources: IDobbiesSearchResultContainerResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    allowBackNavigation?: boolean;
    telemetryContent: ITelemetryContent;
    quickviewSlot?: {} | null | undefined;
    channelInventoryConfigurationId?: number;
    isPriceMinMaxEnabled?: boolean;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    discountLines?: (DiscountLine[] | undefined)[];
    config?: IDobbiesSearchResultContainerConfig;
    showProdPrice?: boolean;
    wishlists: CommerceList[] | undefined;
    isMobile?: boolean;
    loyalty?: LoyaltyCard;
    nonClubPlusDiscount?: ProductPrice[];
    tilesForPLP?: ITilesForPLPData[];
    showTilesOnPLP?: boolean;
    repeatSameTile?: boolean;
    maxNumberOfTiles?: number;
    tileToAppearAfterProducts?: number;
    plpAdsSchedulingData?: ICategoryScheduledAds;
}

export interface IProductWithModes extends ProductSearchResult {
    isCnC?: boolean;
    isHomeDelivery?: boolean;
    clubPlusBanner?: boolean;
}

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

export const ProductSearchResultItems: React.FC<IProductSearchResultItems> = ({
    products,
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    allowBackNavigation,
    telemetryContent,
    quickviewSlot,
    channelInventoryConfigurationId,
    // isPriceMinMaxEnabled,
    isMobile,
    wishlists,
    discountLines,
    config,
    showProdPrice,
    loyalty,
    nonClubPlusDiscount,
    tilesForPLP,
    showTilesOnPLP,
    repeatSameTile,
    maxNumberOfTiles,
    tileToAppearAfterProducts,
    plpAdsSchedulingData
}) => {
    /* Defaulting to show false on both icons while we load the API */
    const initialProducts = useMemo(
        () =>
            products.map(prod => {
                return { ...prod, isCnC: false, isHomeDelivery: false, clubPlusBanner: false };
            }),
        [products]
    );

    const [productsWithDelivery, setProductsWithDelivery] = useState(initialProducts);
    const {
        requestContext: {
            app: {
                config: { fulfillmentAttributeName, fulfillmentAttributeTextValue }
            }
        }
    } = context.actionContext;

    /* VSI Customization - START 14/10/2021 - Method to get Delivery Options and check availability of plant and non plant products */
    // @ts-ignore
    const getDeliveries = async () => {
        const attributeName = fulfillmentAttributeName
            ? fulfillmentAttributeName
                  .toString()
                  .toLowerCase()
                  .trim()
            : 'fulfillmenttype';
        const attributeTextValue = fulfillmentAttributeTextValue
            ? fulfillmentAttributeTextValue
                  .toString()
                  .toLowerCase()
                  .trim()
            : 'plants';

        const plantProducts: ProductSearchResult[] = [];
        const nonPlantProducts: ProductSearchResult[] = [];

        products.map(product => {
            const fulfillmentTypeAttribute =
                product.AttributeValues &&
                product.AttributeValues.find(attribute => {
                    return attribute?.Name?.toLowerCase() === attributeName;
                });
            const isPlantFulfillment = fulfillmentTypeAttribute && fulfillmentTypeAttribute.TextValue?.toLowerCase() === attributeTextValue;
            const isPlantCategory = isPlantFulfillment ? true : false;

            if (isPlantCategory) {
                plantProducts.push(product);
            } else {
                nonPlantProducts.push(product);
            }
        });
        const nonKitProducts = products.filter(prod => prod.IsMasterProduct === false);
        const prodIds = nonKitProducts.map(prod => prod.RecordId);

        // If there are no products, do not make any api calls
        if (prodIds.length <= 0) {
            return;
        }

        const deliveryOptions = await getDeliveryOptionsAsync({ callerContext: context.actionContext, queryResultSettings: {} }, prodIds);

        // Plants API since the warehouse storage is different
        const productAvailabilitesPlants = await getEstimatedAvailabilityAsync(
            { callerContext: context.actionContext },
            { ProductIds: prodIds, DefaultWarehouseOnly: false, FilterByChannelFulfillmentGroup: true }
        );

        const productsWithPlantsDelivery: IProductWithModes[] = await Promise.all(
            plantProducts.map(async prod => {
                let isCnC = false;
                let isHomeDelivery = false;
                const onlyInStoreVal = prod.AttributeValues?.find(
                    attr => attr.Name?.toLocaleLowerCase() === 'onlyinstore'
                )?.TextValue?.toLowerCase();
                if (productAvailabilitesPlants && productAvailabilitesPlants.ProductWarehouseInventoryAvailabilities) {
                    productAvailabilitesPlants.ProductWarehouseInventoryAvailabilities?.map(
                        (item: ProductWarehouseInventoryAvailability) => {
                            if (
                                (item.InventLocationId === 'P001' || item.InventLocationId === 'S059') &&
                                item.ProductId === prod.RecordId
                            ) {
                                if (item.PhysicalAvailableInventoryLevelCode !== 'OOS') {
                                    isHomeDelivery = true;
                                }
                            } else {
                                if (item.PhysicalAvailableInventoryLevelCode !== 'OOS' && item.ProductId === prod.RecordId) {
                                    deliveryOptions
                                        .find(option => option.ProductId === prod.RecordId)
                                        ?.DeliveryOptions?.map(option => {
                                            if (option.Code === 'ClickCol') {
                                                isCnC = true;
                                            }
                                        });
                                }
                            }
                        }
                    );
                }

                // If product has vendorship === "yes" then show it as available
                const isVendorShipAttribute = prod.AttributeValues?.find(attribute => {
                    const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
                    return attributeName === 'isvendershipproduct';
                });

                if (isVendorShipAttribute && isVendorShipAttribute.TextValue && isVendorShipAttribute.TextValue.toLowerCase() === 'yes') {
                    isHomeDelivery = true;
                    isCnC = false;
                }
                if (onlyInStoreVal === 'yes' && isCnC) {
                    isHomeDelivery = false;
                }
                return { ...prod, isHomeDelivery, isCnC };
            })
        );

        // Not Plants API since the warehouse storage is different
        const productAvailabilitesNonPlantsHD = await getEstimatedAvailabilityAsync(
            { callerContext: context.actionContext },
            { ProductIds: prodIds, DefaultWarehouseOnly: true, FilterByChannelFulfillmentGroup: false }
        );
        const productAvailabilitesNonPlantsCNC = await getEstimatedAvailabilityAsync(
            { callerContext: context.actionContext },
            { ProductIds: prodIds, DefaultWarehouseOnly: false, FilterByChannelFulfillmentGroup: true }
        );

        const productsWithNonPlantsDelivery: IProductWithModes[] = await Promise.all(
            nonPlantProducts.map(async prod => {
                let isCnC = false;
                let isHomeDelivery = false;
                const onlyInStoreVal = prod.AttributeValues?.find(
                    attr => attr.Name?.toLocaleLowerCase() === 'onlyinstore'
                )?.TextValue?.toLowerCase();
                if (productAvailabilitesNonPlantsHD && productAvailabilitesNonPlantsHD.AggregatedProductInventoryAvailabilities) {
                    productAvailabilitesNonPlantsHD.AggregatedProductInventoryAvailabilities.map(stores => {
                        if (stores.ProductId === prod.RecordId && stores.PhysicalAvailableInventoryLevelCode !== 'OOS') {
                            isHomeDelivery = true;
                        }
                    });
                }

                if (productAvailabilitesNonPlantsCNC && productAvailabilitesNonPlantsCNC.AggregatedProductInventoryAvailabilities) {
                    productAvailabilitesNonPlantsCNC.AggregatedProductInventoryAvailabilities.map(stores => {
                        if (stores.ProductId === prod.RecordId && stores.PhysicalAvailableInventoryLevelCode !== 'OOS') {
                            deliveryOptions
                                .find(option => option.ProductId === prod.RecordId)
                                ?.DeliveryOptions?.map(option => {
                                    if (option.Code === 'ClickCol') {
                                        isCnC = true;
                                    }
                                });
                        }
                    });
                }

                // If product has vendorship === "yes" then show it as available
                const isVendorShipAttribute = prod.AttributeValues?.find(attribute => {
                    const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
                    return attributeName === 'isvendershipproduct';
                });

                if (isVendorShipAttribute && isVendorShipAttribute.TextValue && isVendorShipAttribute.TextValue.toLowerCase() === 'yes') {
                    isHomeDelivery = true;
                    isCnC = false;
                }
                if (onlyInStoreVal === 'yes' && isCnC) {
                    isHomeDelivery = false;
                }
                return { ...prod, isHomeDelivery, isCnC };
            })
        );

        const totalProducts = [...productsWithPlantsDelivery, ...productsWithNonPlantsDelivery];
        const allProducts: any = [];
        products.map(prod => {
            totalProducts.find(item => {
                if (item.RecordId === prod.RecordId) {
                    allProducts.push(item);
                }
            });
        });
        setProductsWithDelivery(allProducts);
    };

    useEffect(() => {
        const productWithTempDeliveryModes = products.map(prod => {
            return { ...prod, isCnC: false, isHomeDelivery: false, clubPlusBanner: false };
        });

        setProductsWithDelivery(productWithTempDeliveryModes);
        getDeliveries();
        // eslint-disable-next-line
    }, [products.length, products.map(p => p.RecordId).join(',')]);

    // VSI CUSTOMIZATION - These callbacks are now no-ops since banner state is managed locally in ProductComponent
    // Keeping them to maintain prop interface compatibility
    const showClubPlusBanner = useCallback((_prodId: number): void => {
        // No-op: banner state is now managed locally in ProductComponent
    }, []);

    const hideClubPlusBanner = useCallback((_prodId: number): void => {
        // No-op: banner state is now managed locally in ProductComponent
    }, []);

    const getIndexesForTilesToAppearFromFile = (): number[] => {
        const indexes: number[] = [];
        if (plpAdsSchedulingData?.scheduledAds && plpAdsSchedulingData?.scheduledAds.length) {
            for (let i = 0; i < plpAdsSchedulingData?.scheduledAds.length; i++) {
                if (shouldAdBeRendered(plpAdsSchedulingData.scheduledAds[i].startTime, plpAdsSchedulingData.scheduledAds[i].endTime)) {
                    const cuurTileIndex = plpAdsSchedulingData.scheduledAds[i].currentTileShouldAppearAfterProducts;
                    if (cuurTileIndex) {
                        indexes.push(cuurTileIndex);
                    }
                }
            }
        }
        return indexes;
    };

    const getDateFromDateString = (dateString: string): Date => {
        const dateTimeSplitted = dateString.split(' ');
        const startDateMonth = dateTimeSplitted[0].split('/')[1];
        const startDateDay = dateTimeSplitted[0].split('/')[0];
        const startDateYear = dateTimeSplitted[0].split('/')[2];
        const startDateHour = dateTimeSplitted[1].split(':')[0];
        const startDateMinutes = dateTimeSplitted[1].split(':')[1];
        const GMTStr = dateTimeSplitted[dateTimeSplitted.length - 1].includes('GMT') ? dateTimeSplitted[dateTimeSplitted.length - 1] : '';
        const badgeDate = getDateObjFromString(
            `${startDateMonth}-${startDateDay}-${startDateYear} ${startDateHour}:${startDateMinutes}:00 ${GMTStr}`,
            '-',
            true
        );

        return badgeDate;
    };

    const shouldAdBeRendered = (startTime: string, endTime: string): boolean => {
        try {
            const startDate = getDateFromDateString(startTime); //new Date(startDateAttr.TextValue.trim());
            const endDate = getDateFromDateString(endTime); //new Date(endDateAttr.TextValue.trim());
            if (!startDate || !endDate) {
                return true;
            }
            const now = new Date();
            if (now.getTime() <= endDate.getTime() && now.getTime() >= startDate.getTime()) {
                return true;
            }
        } catch (e) {
            return true;
        }
        return false;
    };

    const getTileForIndexFromFile = (index: number, listOfIndexes: number[]): JSX.Element => {
        for (let q = 0; q < listOfIndexes.length; q++) {
            if (listOfIndexes[q] === index) {
                if (plpAdsSchedulingData?.scheduledAds && q < plpAdsSchedulingData?.scheduledAds.length) {
                    return (
                        <div key={plpAdsSchedulingData?.scheduledAds[q].adTitle}>
                            <div className='msc-product' key={plpAdsSchedulingData?.scheduledAds[q].adTitle}>
                                <div className='msc-product__image' key={plpAdsSchedulingData?.scheduledAds[q].adTitle}>
                                    <a
                                        href={plpAdsSchedulingData?.scheduledAds[q].adClickUrl}
                                        style={{ display: 'block', outline: 'none' }}
                                        key={plpAdsSchedulingData?.scheduledAds[q].adTitle}
                                    >
                                        <img
                                            src={plpAdsSchedulingData?.scheduledAds[q].imageUrl}
                                            className='msc-main_image msc_image'
                                            key={plpAdsSchedulingData?.scheduledAds[q].adTitle}
                                        />
                                    </a>
                                </div>
                                <div className='msc-product__content' key={plpAdsSchedulingData?.scheduledAds[q].adClickUrl}>
                                    <div className='msc-product__details' key={plpAdsSchedulingData?.scheduledAds[q].adClickUrl}>
                                        <a
                                            href={plpAdsSchedulingData?.scheduledAds[q].adClickUrl}
                                            key={plpAdsSchedulingData?.scheduledAds[q].adClickUrl}
                                        >
                                            <div className='msc-product__details'>
                                                <h4 title={plpAdsSchedulingData?.scheduledAds[q].adTitle} className='msc-product__title'>
                                                    {plpAdsSchedulingData?.scheduledAds[q].adTitle}
                                                </h4>
                                            </div>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                }
            }
        }
        return <></>;
    };

    const productClass = 'ms-product-search-result__item';
    if (showTilesOnPLP && plpAdsSchedulingData?.scheduledAds && plpAdsSchedulingData?.scheduledAds.length) {
        let numOfTilesShown = 0;
        const productsList: JSX.Element[] = [];
        const indexesForTilesToAppear = getIndexesForTilesToAppearFromFile();
        for (let prodIndex = 0; prodIndex < productsWithDelivery.length; prodIndex++) {
            const product = productsWithDelivery[prodIndex];
            const productElem = (
                <ProductComponent
                    key={product.RecordId}
                    context={context}
                    telemetryContent={telemetryContent}
                    imageSettings={imageSettings}
                    freePriceText={resources.priceFree}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    ratingAriaLabel={resources.ratingAriaLabel}
                    allowBack={allowBackNavigation}
                    id={moduleId}
                    typeName={moduleType}
                    data={{ product: product }}
                    inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                    discountLine={discountLines && discountLines.length > 0 ? discountLines[prodIndex] : []}
                    badgeLink={config && config.clubPlusBadgeLink}
                    showProdPrice={showProdPrice}
                    showBanner={showClubPlusBanner}
                    hideBanner={hideClubPlusBanner}
                    quickViewButton={quickviewSlot}
                    wishlists={wishlists}
                    isMobile={isMobile}
                    loyalty={loyalty}
                    nonClubPlusDiscount={nonClubPlusDiscount?.filter(item => item.ProductId === product.RecordId)[0]}
                />
            );
            productsList.push(productElem);
        }
        for (let b = 0; b < productsWithDelivery.length; b++) {
            if (repeatSameTile && tileToAppearAfterProducts && maxNumberOfTiles && b > 0) {
                const quotient = b / tileToAppearAfterProducts;
                const isRepeatTileIndex = quotient % 1 === 0;
                if (isRepeatTileIndex && numOfTilesShown < maxNumberOfTiles) {
                    const elem = getTileForIndexFromFile(b, indexesForTilesToAppear);
                    numOfTilesShown++;
                    productsList.push(elem);
                }
            } else if (indexesForTilesToAppear.includes(b)) {
                const elem = getTileForIndexFromFile(b, indexesForTilesToAppear);
                numOfTilesShown++;
                productsList.splice(b, 0, elem);
            }
        }
        const finalUL = (
            <ul className='list-unstyled'>
                {productsList.map((item, index) => {
                    return (
                        <li className={productClass} key={item.key || index}>
                            {item}
                        </li>
                    );
                })}
            </ul>
        );
        return finalUL;
    }
    return (
        <ul className='list-unstyled'>
            {productsWithDelivery &&
                productsWithDelivery.map((product: IProductWithModes, index: number) => {
                    return (
                        <li className={productClass} key={product.RecordId} aria-label={`${product.RecordId}`}>
                            <ProductComponent
                                key={product.RecordId}
                                context={context}
                                telemetryContent={telemetryContent}
                                imageSettings={imageSettings}
                                freePriceText={resources.priceFree}
                                originalPriceText={resources.originalPriceText}
                                currentPriceText={resources.currentPriceText}
                                ratingAriaLabel={resources.ratingAriaLabel}
                                allowBack={allowBackNavigation}
                                id={moduleId}
                                typeName={moduleType}
                                data={{ product: product }}
                                inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                                discountLine={discountLines && discountLines.length > 0 ? discountLines[index] : []}
                                badgeLink={config && config.clubPlusBadgeLink}
                                showProdPrice={showProdPrice}
                                showBanner={showClubPlusBanner}
                                hideBanner={hideClubPlusBanner}
                                quickViewButton={quickviewSlot}
                                wishlists={wishlists}
                                isMobile={isMobile}
                                loyalty={loyalty}
                                nonClubPlusDiscount={nonClubPlusDiscount?.filter(item => item.ProductId === product.RecordId)[0]}
                            />
                        </li>
                    );
                })}
        </ul>
    );
    /* VSI Customization - END */
};
